import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useAuthStore } from '@/stores/authStore';

/**
 * The records and saved views you opened lately, newest first, for Home and for
 * Cmd+K before you type.
 *
 * Kept in the browser, one list per user under `recent-items:v1:<user id>`, so on
 * a shared office computer one person's records never show to the next. Log out
 * clears the list. A record that turns out not to exist is dropped when opened.
 */

export const RECENT_ITEMS_LIMIT = 20;

export interface RecentItem {
    kind: 'record' | 'view';
    /** The entity the record or view belongs to, for example `LocationZonePlayer`. */
    entityId: string;
    id: number;
    label: string;
    /** Milliseconds since the epoch. */
    openedAt: number;
}

function storageKey(userId: number | string): string {
    return `recent-items:v1:${userId}`;
}

function isRecentItem(value: unknown): value is RecentItem {
    if (typeof value !== 'object' || value === null) return false;
    const item = value as Record<string, unknown>;
    return (
        (item.kind === 'record' || item.kind === 'view') &&
        typeof item.entityId === 'string' &&
        typeof item.id === 'number' &&
        typeof item.label === 'string' &&
        typeof item.openedAt === 'number'
    );
}

function read(userId: number | string): RecentItem[] {
    try {
        const parsed: unknown = JSON.parse(localStorage.getItem(storageKey(userId)) ?? '[]');
        return Array.isArray(parsed) ? parsed.filter(isRecentItem).slice(0, RECENT_ITEMS_LIMIT) : [];
    } catch {
        return [];
    }
}

function write(userId: number | string, items: RecentItem[]): void {
    try {
        localStorage.setItem(storageKey(userId), JSON.stringify(items));
    } catch {
        // Storage can be full or blocked. Recent items are a convenience; losing them is fine.
    }
}

const sameItem = (a: Pick<RecentItem, 'kind' | 'entityId' | 'id'>, b: Pick<RecentItem, 'kind' | 'entityId' | 'id'>) =>
    a.kind === b.kind && a.entityId === b.entityId && a.id === b.id;

export const useRecentItemsStore = defineStore('recentItems', () => {
    const authStore = useAuthStore();
    const userId = computed(() => authStore.userData?.id ?? null);
    const items = ref<RecentItem[]>([]);

    watch(
        userId,
        (current) => {
            items.value = current === null ? [] : read(current);
        },
        // Synchronous, so the list changes the moment the signed-in user does and
        // one person's items are never read, or written, under the next person.
        { immediate: true, flush: 'sync' },
    );

    function save(next: RecentItem[]) {
        items.value = next;
        if (userId.value !== null) {
            write(userId.value, next);
        }
    }

    function remember(item: Omit<RecentItem, 'openedAt'> & { openedAt?: number }) {
        const entry: RecentItem = { ...item, openedAt: item.openedAt ?? Date.now() };
        save([entry, ...items.value.filter((existing) => !sameItem(existing, entry))].slice(0, RECENT_ITEMS_LIMIT));
    }

    function forget(entityId: string, id: number, kind: RecentItem['kind'] = 'record') {
        save(items.value.filter((existing) => !sameItem(existing, { kind, entityId, id })));
    }

    function clearForCurrentUser() {
        items.value = [];
        if (userId.value !== null) {
            try {
                localStorage.removeItem(storageKey(userId.value));
            } catch {
                // See write().
            }
        }
    }

    return { items, remember, forget, clearForCurrentUser };
});

export interface RecentItemGroup {
    label: 'Today' | 'Yesterday' | 'Earlier';
    items: RecentItem[];
}

/** Recent items under Today, Yesterday and Earlier, by the browser's own calendar day. */
export function groupRecentItemsByDay(items: RecentItem[], now: number = Date.now()): RecentItemGroup[] {
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    const todayStart = startOfToday.getTime();
    const yesterdayStart = new Date(startOfToday).setDate(startOfToday.getDate() - 1);

    const groups: RecentItemGroup[] = [
        { label: 'Today', items: items.filter((item) => item.openedAt >= todayStart) },
        { label: 'Yesterday', items: items.filter((item) => item.openedAt >= yesterdayStart && item.openedAt < todayStart) },
        { label: 'Earlier', items: items.filter((item) => item.openedAt < yesterdayStart) },
    ];
    return groups.filter((group) => group.items.length > 0);
}
